import { cn } from '../lib/utils'
import { COLOR_HEX } from '../constants'
import { GolfBall, Input } from './ui'

export function FullScorecard({ players, holes, currentHole = null }) {
  const scoreAt = (holeNumber, playerId) => {
    const h = holes.find((x) => x.number === holeNumber)
    const s = h?.scores.find((x) => x.playerId === playerId)
    return s?.strokes
  }

  const totals = players.map((p) =>
    holes.reduce((sum, h) => {
      const s = h.scores.find((x) => x.playerId === p.id)
      return sum + (s?.strokes || 0)
    }, 0)
  )

  const gridCols = {
    gridTemplateColumns: `minmax(2.5rem, 0.7fr) repeat(${players.length}, minmax(0, 1fr))`,
  }

  const rowHighlight = (isActive) =>
    cn('border-t border-border/50', isActive && 'bg-primary/10')

  return (
    <div className="rounded-lg border border-border/60 bg-card shadow-paper-sm">
      <div className="grid" style={gridCols}>
        <div className="py-1.5 text-xs font-medium text-muted-foreground"></div>
        {players.map((player) => (
          <div
            key={player.id}
            className="px-1 h-16 text-sm font-medium flex items-center justify-center whitespace-nowrap"
            style={{ transform: 'rotate(-45deg)', transformOrigin: 'center' }}
          >
            <span className="truncate">{player.name}</span>
          </div>
        ))}

        {holes.map((h) => {
          const isActive = h.number === currentHole
          return (
            <div key={h.number} className="contents">
              <div className={cn('px-2 py-2.5 text-left', rowHighlight(isActive))}>
                <div className="text-base font-medium">{h.number}</div>
                <div className="text-xs text-muted-foreground">par {h.par ?? 3}</div>
              </div>
              {players.map((player) => {
                const strokes = scoreAt(h.number, player.id)
                return (
                  <div
                    key={`${player.id}-${h.number}`}
                    className={cn(
                      'px-1 py-2.5 text-center text-xl font-hand',
                      rowHighlight(isActive),
                      isActive ? 'text-primary' : 'text-foreground'
                    )}
                  >
                    {strokes ?? ''}
                  </div>
                )
              })}
            </div>
          )
        })}

        <div className={cn('px-2 py-2.5 text-left text-sm font-medium text-muted-foreground', 'border-t border-border/50 flex items-center')}>
          Totaal
        </div>
        {totals.map((t, pi) => (
          <div
            key={players[pi].id}
            className="px-1 py-2.5 text-center text-xl font-hand text-primary border-t border-border/50 flex items-center justify-center"
          >
            {t}
          </div>
        ))}
      </div>
    </div>
  )
}

export function ScorecardTable({
  players,
  holes,
  currentHole = 1,
  editable = false,
  scores = {},
  onScoreChange = null,
}) {
  const hasPrev = currentHole > 1
  const prevNumber = currentHole - 1
  const currentNumber = currentHole

  const scoreAt = (holeNumber, playerId) => {
    const h = holes.find((x) => x.number === holeNumber)
    const s = h?.scores.find((x) => x.playerId === playerId)
    return s?.strokes
  }

  const totals = players.map((p) =>
    holes.reduce((sum, h) => {
      const s = h.scores.find((x) => x.playerId === p.id)
      return sum + (s?.strokes || 0)
    }, 0)
  )

  const gridCols = hasPrev
    ? { gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.8fr)' }
    : { gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 0.8fr)' }

  const active = 'bg-primary/10 text-primary'
  const holeCell = (isActive, value) =>
    cn(
      'px-1 py-3 text-center text-3xl font-hand border-t border-border/50',
      isActive ? active : 'text-foreground'
    )

  const currentCell = (isActive, value, playerId) =>
    editable && onScoreChange ? (
      <div
        className={cn(
          'px-1 py-2 border-t border-border/50 flex items-center justify-center',
          isActive && 'bg-primary/10'
        )}
      >
        <Input
          type="number"
          min="0"
          max="8"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="0"
          value={scores[playerId] ?? ''}
          onChange={(e) => onScoreChange(playerId, e.target.value)}
          className="h-12 w-16 text-center text-3xl font-hand"
        />
      </div>
    ) : (
      <div className={holeCell(isActive, value)}>{value ?? ''}</div>
    )

  return (
    <div className="rounded-lg border border-border/60 bg-card shadow-paper-sm">
      <div className="grid" style={gridCols}>
        <div className="py-1.5 text-xs font-medium text-muted-foreground"></div>
        {hasPrev && (
          <div className="px-1 py-1.5 text-xs font-medium text-center text-muted-foreground">
            Vorige
          </div>
        )}
        <div className="px-1 py-1.5 text-xs font-medium text-center text-primary">Huidig</div>
        <div className="px-1 py-1.5 text-xs font-medium text-center text-muted-foreground">
          Totaal
        </div>

        {players.map((player, pi) => (
          <div key={player.id} className="contents">
            <div className="flex items-center gap-1.5 px-3 py-3 border-t border-border/50">
              <GolfBall color={COLOR_HEX[player.selectedColor]} />
              <span className="text-base font-medium truncate">{player.name}</span>
            </div>
            {hasPrev && (
              <div className={holeCell(false, scoreAt(prevNumber, player.id))}>
                {scoreAt(prevNumber, player.id) ?? '–'}
              </div>
            )}
            {currentCell(true, scoreAt(currentNumber, player.id), player.id)}
            <div className={cn('px-1 py-3 text-center text-3xl font-hand text-primary border-t border-border/50')}>
              {totals[pi]}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}